import { parse, DATE_FORMAT } from '@eneco/common/date';
import { useFormatter } from '@eneco/i18n';
import * as yup from 'yup';

export const DELIVERY_DATE_OPTIONS = ['ASAP', 'CONTRACT_END', 'CUSTOM'] as const;

export type DeliveryDateOption = (typeof DELIVERY_DATE_OPTIONS)[number];

/**
 * One field per option rather than a single shared `deliveryDate`: switching options must never
 * carry a date across semantics (`einddatum` → `startdatum`), and each field owns its own error.
 * The context's `deliveryDate` is derived from these at submit — see `useDeliveryDateOptions_B`.
 */
export interface DeliveryDateFormFieldsB {
  deliveryDateOption?: DeliveryDateOption;
  /** dd-MM-yyyy, only filled for `CONTRACT_END`. */
  contractEndDate?: string;
  /** dd-MM-yyyy, only filled for `CUSTOM`. */
  customStartDate?: string;
  consentConsiderationPeriod?: boolean;
}

interface DeliveryDateFormFieldTextualData {
  requiredMessage?: string;
  validationMinValueMessage?: string;
  validationMaxValueMessage?: string;
}

interface DeliveryDateSchemaTextualData {
  deliveryDate?: {
    deliveryDateFormField?: { value?: DeliveryDateFormFieldTextualData };
    isUrgentFormField?: { value?: { requiredMessage?: string } };
  };
}

interface BuildDeliveryDateOptionSchemaParams {
  isMoving?: boolean;
  minimumDate: Date;
  maximumDate: Date;
  minimumDays: number;
  /** Group-level message when no option is picked — the only message not backed by a Sitecore field. */
  groupRequiredMessage: string;
  /** Moving, non-SME customers are the only ones who can be asked to waive the cooling-off period. */
  isConsentEligible: boolean;
  resolveDeliveryDate: (fields: DeliveryDateFormFieldsB) => string | undefined;
  isWithinConsiderationPeriod: (deliveryDate?: string) => boolean;
  format: ReturnType<typeof useFormatter>['format'];
  dateShort: ReturnType<typeof useFormatter>['date']['short'];
  textualData?: DeliveryDateSchemaTextualData;
}

/**
 * exp-n-630 variant schema: the option itself is required, and each option's date field is only
 * validated while that option is selected. All validation messages come from the existing Sitecore
 * fields so the experiment invents no validation copy; only the group-level message is new.
 */
export const buildDeliveryDateOptionSchema_B = ({
  isMoving,
  minimumDate,
  maximumDate,
  minimumDays,
  groupRequiredMessage,
  isConsentEligible,
  resolveDeliveryDate,
  isWithinConsiderationPeriod,
  format,
  dateShort,
  textualData,
}: BuildDeliveryDateOptionSchemaParams) => {
  const fieldTextualData = textualData?.deliveryDate?.deliveryDateFormField?.value;

  const requiredMessage = fieldTextualData?.requiredMessage;
  const consentMessage = textualData?.deliveryDate?.isUrgentFormField?.value?.requiredMessage;

  // The Sitecore min-value message spells out the switching window; for moving customers the window
  // is shorter, so the control falls back to the required message rather than quoting a wrong
  // number of days. Kept verbatim here.
  const minValueMessage = isMoving
    ? (requiredMessage ?? '')
    : format(fieldTextualData?.validationMinValueMessage ?? '', { minimumDays });

  const maxValueMessage = format(fieldTextualData?.validationMaxValueMessage ?? '', {
    maximumDate: dateShort(maximumDate),
  });

  const isNotAfterMaximum = (currentDate?: string) => !!currentDate && parse(currentDate, DATE_FORMAT) <= maximumDate;
  const isNotBeforeMinimum = (currentDate?: string) => !!currentDate && parse(currentDate, DATE_FORMAT) >= minimumDate;

  return yup.object({
    deliveryDateOption: yup.string().oneOf(DELIVERY_DATE_OPTIONS).required(groupRequiredMessage),

    // A contract end date may legitimately fall before the earliest date we can start delivering,
    // so this field is deliberately exempt from the minimum — the customer gets the
    // `Nooit zonder energie` notification instead of a validation error.
    contractEndDate: yup.string().when('deliveryDateOption', {
      is: 'CONTRACT_END',
      then: schema => schema.required(requiredMessage).test({ message: maxValueMessage, test: isNotAfterMaximum }),
    }),

    customStartDate: yup.string().when('deliveryDateOption', {
      is: 'CUSTOM',
      then: schema =>
        schema
          .required(requiredMessage)
          .test({ message: minValueMessage, test: isNotBeforeMinimum })
          .test({ message: maxValueMessage, test: isNotAfterMaximum }),
    }),

    // Required exactly when the checkbox is on screen: derived from the same three fields the
    // rendered card reads, so the schema and the UI can never disagree about whether consent is due.
    consentConsiderationPeriod: yup.boolean().when(
      ['deliveryDateOption', 'contractEndDate', 'customStartDate'],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ([deliveryDateOption, contractEndDate, customStartDate]: any[], schema) =>
        isConsentEligible &&
        isWithinConsiderationPeriod(resolveDeliveryDate({ deliveryDateOption, contractEndDate, customStartDate }))
          ? schema
              .required(consentMessage)
              .test('true', consentMessage ?? '', (value: boolean | undefined) => !!value)
          : schema,
    ),
  });
};
